
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const FloatingButtons = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsApp = () => {
    window.open('https://wa.me/33554542545', '_blank', 'noopener,noreferrer');
  };

  const handleCall = () => {
    window.location.href = 'tel:0554542545';
  };

  const handleAIAgent = () => {
    const aiButton = document.querySelector('#vapi-widget-floating-button') as HTMLElement;
    if (aiButton) {
      aiButton.click();
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, x: 20, scale: 0.8 },
    visible: (i: number) => ({
      opacity: 1,
      x: 0,
      scale: 1,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
        ease: "easeOut"
      }
    }),
    exit: { opacity: 0, x: 20, scale: 0.8, transition: { duration: 0.2 } }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3" role="complementary" aria-label="Boutons de contact flottants">
          {/* Expanded Buttons */}
          <AnimatePresence>
            {isExpanded && (
              <>
                {/* WhatsApp Button */}
                <motion.button
                  custom={0}
                  variants={buttonVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={handleWhatsApp}
                  className="group flex items-center gap-3 bg-[#25D366] hover:bg-[#20BA5A] text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  whileHover={{ scale: 1.05, x: -5 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Contacter via WhatsApp"
                >
                  <span className="text-sm font-semibold whitespace-nowrap">WhatsApp</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-whatsapp-line text-2xl"></i>
                  </div>
                </motion.button>

                {/* Phone Button */}
                <motion.button
                  custom={1}
                  variants={buttonVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={handleCall}
                  className="group flex items-center gap-3 bg-orange-500 hover:bg-orange-600 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  whileHover={{ scale: 1.05, x: -5 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Appeler maintenant au 05 54 54 25 45"
                >
                  <span className="text-sm font-semibold whitespace-nowrap">Appeler</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-phone-line text-2xl"></i>
                  </div>
                </motion.button>

                {/* AI Agent Button */}
                <motion.button
                  custom={2}
                  variants={buttonVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  onClick={handleAIAgent}
                  className="group flex items-center gap-3 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white px-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  whileHover={{ scale: 1.05, x: -5 }}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Ouvrir l'assistant IA"
                >
                  <span className="text-sm font-semibold whitespace-nowrap">Assistant IA</span>
                  <div className="w-10 h-10 flex items-center justify-center">
                    <i className="ri-customer-service-2-line text-2xl"></i>
                  </div>
                </motion.button>
              </>
            )}
          </AnimatePresence>

          {/* Main Toggle Button */}
          <motion.button
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            onClick={() => setIsExpanded(!isExpanded)}
            className={`relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center transition-all cursor-pointer ${
              isExpanded
                ? 'bg-gray-800 hover:bg-gray-900'
                : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700'
            }`}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label={isExpanded ? 'Fermer le menu de contact' : 'Ouvrir le menu de contact'}
            aria-expanded={isExpanded}
          >
            <motion.i
              animate={{ rotate: isExpanded ? 45 : 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className={`${isExpanded ? 'ri-close-line' : 'ri-message-3-line'} text-white text-3xl`}
            ></motion.i>
          </motion.button>

          {/* Pulse Animation when closed */}
          {!isExpanded && (
            <>
              <motion.div
                className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-teal-500 opacity-30 pointer-events-none"
                animate={{
                  scale: [1, 1.4, 1],
                  opacity: [0.3, 0, 0.3]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute bottom-0 right-0 w-16 h-16 rounded-full bg-teal-400 opacity-20 pointer-events-none"
                animate={{
                  scale: [1, 1.6, 1],
                  opacity: [0.2, 0, 0.2]
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />
            </>
          )}
        </div>
      )}
    </AnimatePresence>
  );
};

export default FloatingButtons;
